import { createAsyncThunk } from '@reduxjs/toolkit'
import {
  maskPhoneNumber,
  maskWithLast4Digits,
  normalizeEmail
} from 'helpers/fieldFormatters'
import { getSectionOfArray } from 'helpers/types/Array'
import buyerAPI from 'services/buyer'
import {
  AddBuyerRequest,
  BuyerInfoResponse,
  DeleteBuyerRequest,
  EditBuyerInfo,
  ExtendedNewBuyer,
  LostOrStolen
} from 'typings/buyer'
import { BuyerInfo, BuyerInfoState } from 'typings/reduxStore'
import { ServerError } from 'typings/serverError'
import type { OriginalRootState } from 'redux/rootReducer'
import { selectAccessToken } from 'redux/authToken/authTokenSlice'
// eslint-disable-next-line import/no-cycle
import { updateBuyerState } from './buyerSlice'

export const getBuyerList = createAsyncThunk(
  'buyerSlice/getBuyerList',
  async (payload: BuyerInfoState, { dispatch }) => {
    try {
      const response = await buyerAPI.getBuyers(payload)

      const { currentPage, limit } = payload

      let { contactDetails } = response

      /*
       *     Support: `null` or `undefined` Values
       * Description: While default parameters will trigger for `undefined` values,
       *              they DON'T trigger for `null`.
       *         Fix: Perform OR conditional check here that handles both
       *              `undefined` and null values.
       */
      contactDetails = contactDetails || []

      /*
       *     Support: Grid Pagination
       * Description: API support for grid pagination is not provided here unlike
       *              other API calls used. Can only return full list.
       *         Fix: Integrate pagination logic here until support for this is
       *              added on the Back-end.
       */
      const dataList = getSectionOfArray(contactDetails, currentPage, limit)

      /*
       *     Support: Missing Object Props
       * Description: The <GenericGrid> doesn't have the logic for pulling in
       *              multiple properties into one column. In addition, the API
       *              is unable to be modified to add a new prop that combines
       *              both first and last names.
       *         Fix: Add `name` prop to combine first and last names.
       */
      const buyers = dataList.map((item: ExtendedNewBuyer) => ({
        ...item,
        name: `${item.firstName} ${item.lastName}`,
        email: `${normalizeEmail(item?.emailAddress || '')}`,
        phone: item?.phoneNumber ? `+${maskPhoneNumber(item.phoneNumber)}` : ''
      }))

      dispatch(
        updateBuyerState({
          dataList: buyers,
          buyerCount: contactDetails.length,
          gridOption: { currentPage, limit },
          errorMessage: '',
          isBuyerResponseFail: false,
          isDataLoading: false
        })
      )
    } catch (errorResponse) {
      const { response } = errorResponse as ServerError
      const { message } = response || {}

      dispatch(
        updateBuyerState({
          errorMessage: message,
          isBuyerResponseFail: true,
          dataList: [],
          isDataLoading: false,
          error: true
        })
      )
    }
  }
)

export const addBuyer = createAsyncThunk(
  'buyerSlice/addBuyer',
  async (payload: AddBuyerRequest, { dispatch }) => {
    try {
      dispatch(
        updateBuyerState({
          isLoading: true,
          newBuyer: { ...payload.newBuyer }
        })
      )
      const response = await buyerAPI.addBuyer(payload)

      const { statusCode } = response
      let buyerInfo: Array<BuyerInfo> = []

      if (statusCode === 207) {
        buyerInfo = response?.buyerInfo?.map(
          ({
            lastFourDigitAccountNumber,
            status,
            buyer: { card }
          }: BuyerInfoResponse) => {
            return {
              accountNo: lastFourDigitAccountNumber,
              name: `(${maskWithLast4Digits(
                lastFourDigitAccountNumber,
                '•',
                3
              )})`,
              isSendCard: card?.toString() === 'Y',
              status
            }
          }
        )
      }

      dispatch(
        updateBuyerState({
          isLoading: false,
          addBuyerStatus: statusCode === 207 ? 'PARTIAL' : 'SUCCESS',
          buyerInfo
        })
      )
    } catch (errorResponse) {
      const { response } = errorResponse as ServerError
      const { message } = response || {}
      dispatch(
        updateBuyerState({
          errorMessage: message,
          isLoading: false,
          addBuyerStatus: response?.businessErrors[0]?.message
        })
      )
    }
  }
)

export const deleteBuyer = createAsyncThunk(
  'buyerSlice/deleteBuyer',
  async (payload: DeleteBuyerRequest, { dispatch }) => {
    try {
      const response = await buyerAPI.deleteBuyer(payload)

      const {
        authBuyerUpdate: { status }
      } = response

      const deleteBuyerResponse =
        status === 'S'
          ? { removeBuyersStatus: 'success' }
          : {
              removeBuyersStatus: 'error',
              error: true,
              errorMessage: status
            }

      dispatch(
        updateBuyerState({ ...deleteBuyerResponse, ...{ isLoading: false } })
      )
    } catch (errorResponse) {
      const { response } = errorResponse as ServerError
      const { message } = response || {}

      dispatch(
        updateBuyerState({
          removeBuyersStatus: 'error',
          error: true,
          errorMessage: message,
          isLoading: false
        })
      )
    }
  }
)

interface EditBuyerPayload {
  buyerInfo: EditBuyerInfo
}

export const editBuyer = createAsyncThunk(
  'buyerSlice/editBuyer',
  async ({ buyerInfo }: EditBuyerPayload, { getState, rejectWithValue }) => {
    try {
      const state = getState() as OriginalRootState
      // fixes bug when user updates contact Info multiple times without hiding the buyer between updates
      const buyerState = state.buyer

      const accessToken = selectAccessToken(state)

      // find up to date contactPointId first
      const contactPointId = buyerState.dataList.find(
        b => b.accountRoleId === buyerInfo.authBuyer[0]?.roleId
      )?.contactPointID

      const newBuyerInfo = { ...buyerInfo }

      const hasPhoneDetails = Boolean(
        newBuyerInfo.authBuyer[0].contactDetails.phoneDetails
      )

      if (hasPhoneDetails && contactPointId) {
        newBuyerInfo.authBuyer[0].contactDetails.phoneDetails.contactPointID =
          contactPointId
      }

      const response = await buyerAPI.editBuyer({
        accessToken,
        buyerInfo: newBuyerInfo
      })

      const { status } = response.authBuyerUpdate
      return { status }
    } catch (errorResponse) {
      return rejectWithValue(errorResponse)
    }
  }
)

export const orderReplacementCards = createAsyncThunk(
  'buyerSlice/orderReplacementCards',
  async (payload: BuyerInfoState, { dispatch }) => {
    try {
      const response = await buyerAPI.orderReplacementCards(payload)

      const { lostOrStolen } = response

      const noneLostOrStolen = Boolean(
        lostOrStolen.find(
          ({ status }: LostOrStolen) =>
            status.toLowerCase() === 'no data for update'
        )
      )

      dispatch(
        updateBuyerState({
          orderCardsStatus: 'success',
          orderCardsState: {
            lostOrStolen: noneLostOrStolen ? [] : [...lostOrStolen]
          },
          isLoading: false
        })
      )
    } catch (errorResponse) {
      const { response } = errorResponse as ServerError
      const { message } = response || {}

      dispatch(
        updateBuyerState({
          orderCardsStatus: 'error',
          error: true,
          errorMessage: message,
          isLoading: false
        })
      )
    }
  }
)
